import { PROFANITY_RULES, PROFANITY_WHITELIST, ProfanityRule } from '@constants/profanity';

const CHOSUNG = [
    'ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ',
    'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
];

const JUNGSUNG = [
    'ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ',
    'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ',
];

const JONGSUNG = [
    '', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ',
    'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
];

const HANGUL_BASE = 0xac00;

// 조합형 자모 (U+1100~) 범위
const CHOSUNG_BASE = 0x1100;
const JUNGSUNG_BASE = 0x1161;
const JONGSUNG_BASE = 0x11a7;

// 한글 음절 / 자모 / 영문만 남긴다 (공백, 특수문자, 숫자, 이모지 제거)
const nonLetterRegExp = /[^가-힣ㄱ-ㅎㅏ-ㅣa-z]/g;
// 3회 이상 반복되는 문자를 한 글자로 축약한다 (ㅆㅆㅆㅂ → ㅆㅂ)
const repeatedRegExp = /(.)\1{2,}/g;

/**
 * 조합형 자모를 호환 자모로 되돌린다. (U+1109 → ㅅ)
 * 분해된 형태로 입력되어 음절로 합쳐지지 못한 자모를 살리기 위해 사용한다.
 * @param char
 */
function toCompatibilityJamo(char: string) {
    const code = char.charCodeAt(0);
    if (code >= CHOSUNG_BASE && code < CHOSUNG_BASE + CHOSUNG.length) {
        return CHOSUNG[code - CHOSUNG_BASE];
    }
    if (code >= JUNGSUNG_BASE && code < JUNGSUNG_BASE + JUNGSUNG.length) {
        return JUNGSUNG[code - JUNGSUNG_BASE];
    }
    if (code > JONGSUNG_BASE && code < JONGSUNG_BASE + JONGSUNG.length) {
        return JONGSUNG[code - JONGSUNG_BASE];
    }
    return char;
}

/**
 * 분리해서 입력한 자모를 음절로 합친다. (ㅅㅣ발 → 시발, ㅂㅕㅇㅅㅣㄴ → 병신)
 * @param value
 */
function composeJamo(value: string) {
    const composed: string[] = [];
    let index = 0;
    while (index < value.length) {
        const cho = CHOSUNG.indexOf(value[index]);
        const jung = JUNGSUNG.indexOf(value[index + 1] ?? '');
        if (cho < 0 || jung < 0) {
            composed.push(value[index]);
            index += 1;
        } else {
            // 뒤따르는 자음이 종성인지, 다음 글자의 초성인지는 그 다음 글자로 판단한다
            const isNextSyllable = JUNGSUNG.includes(value[index + 3] ?? '');
            const jong = isNextSyllable ? -1 : JONGSUNG.indexOf(value[index + 2] ?? '');
            const jongIndex = jong > 0 ? jong : 0;
            composed.push(String.fromCharCode(HANGUL_BASE + (cho * 21 + jung) * 28 + jongIndex));
            index += jongIndex > 0 ? 3 : 2;
        }
    }
    return composed.join('');
}

/**
 * 금칙어 검사를 위해 입력값을 정규화한다.
 * 공백/특수문자/숫자/반복문자/자모 분리를 이용한 우회 표현을 원형으로 되돌린다.
 * @param value
 */
export function normalizeProfanityText(value: string) {
    // NFKC 는 호환 자모(ㅅ)를 조합형 자모로 바꿔 버리므로 NFC 를 사용한다
    const stripped = [...value.normalize('NFC').toLowerCase()]
        .map(toCompatibilityJamo)
        .join('')
        .replace(nonLetterRegExp, '')
        .replace(repeatedRegExp, '$1');
    const composed = composeJamo(stripped);
    // 정상 단어를 먼저 걷어내 오탐을 막는다
    return PROFANITY_WHITELIST.reduce(
        (text, allowed) => text.split(allowed).join(''),
        composed,
    );
}

/**
 * 입력값에서 탐지된 금칙어 규칙
 * @param value
 */
export function detectProfanity(value: string): ProfanityRule | undefined {
    const normalized = normalizeProfanityText(value);
    if (!normalized) {
        return undefined;
    }
    return PROFANITY_RULES.find(({ regex }) => regex.test(normalized));
}

export function containsProfanity(value: string) {
    return !!detectProfanity(value);
}
